import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { Box,Button,Checkbox,Dialog,DialogActions,DialogContent,DialogTitle,FormControlLabel,IconButton,InputAdornment,Skeleton,Slider,Stack,Tab,Tabs,TextField,Typography } from '@mui/material';
import { useMutation,useQuery } from '@tanstack/react-query';
import React,{ useContext,useRef,useState } from 'react';
import AvatarEditor from 'react-avatar-editor';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UserContext } from '../../../context/UserContext';
import { AuthContext } from '../../../context/AuthContext';
import AttToken from '../../../helpers/attToken';
import { trackEventMatomo } from '../../../helpers/matomo';
import { authService } from '../../../service/authApi';
import Companies from '../../../service/Companies';
import Login from '../../../service/Login';
import queryClient from '../../../service/query';
import Teams from '../../../service/Teams';
import Users from '../../../service/Users';
import { TabPanelProps } from '../../../types/helpers/types';
import ModalDelete from '../Modal/ModalDelete';
import EditUser from './EditUser';
import Loading from './Loading';
import { SessionInfo } from './SessionInfo';
import type { PasswordRules, userType } from '../../../types/users/types';
import type { SimpleTeam } from '../../../types/teams/types';
import LockResetIcon from '@mui/icons-material/LockReset';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const companySrvc = new Companies();
const loginService = new Login();

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ModalImage = ({ openModal, setOpenModal, setUser, user }: {
  openModal: boolean,
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>,
  setUser: React.Dispatch<React.SetStateAction<userType | null>>,
  user: userType
}) => {
  const { t } = useTranslation('translation');
  const avatarEditorRef = useRef<AvatarEditor | null>(null);
  const [scale, setScale] = useState<number>(1);

  const handleChange = (newValue: number | number[]) => {
    trackEventMatomo('modal edição de usuário', 'click', 'input', 'seleciona imagem');
    if (typeof newValue === 'number') {
      setScale(newValue);
    }
  };

  function getBase64(file: File, cb: (s: string) => void) {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      cb(reader.result as string);
    };
    reader.onerror = function (error) {
      console.error('Error: ', error);
    };
  }

  return (
    <Dialog
      fullWidth
      maxWidth={'sm'}
      open={openModal}
      onClose={() => setOpenModal(false)}
      scroll={'paper'}
    >
      <DialogTitle>{t('editUserModal.imageEdit')}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => setOpenModal(false)}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers={true}>
        <Stack
          direction="column"
          spacing={2}
          sx={{
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <AvatarEditor
            image={user?.foto ?? ''}
            width={200}
            height={200}
            border={10}
            borderRadius={200}
            color={[243, 243, 244, 0.6]}
            scale={scale}
            rotate={0}
            ref={avatarEditorRef}
          />
          <Stack
            direction="row"
            spacing={1}
            sx={{
              justifyContent: "center",
              alignItems: "center",
              width: '100%'
            }}
          >
            <Typography color='textDisabled' fontWeight={600}>Zoom: </Typography>
            <Slider
              aria-label="Always visible"
              defaultValue={100}
              step={0.1}
              min={1}
              max={5}
              value={scale}
              style={{ width: '30%', color: '#47aa8d' }}
              onChange={(eve, value) => {
                eve.preventDefault();
                handleChange(value);
              }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction='row' width='100%' justifyContent='space-between' alignItems='center' spacing={2}>
          <Button
            size='small'
            startIcon={<CloudUploadIcon />}
            style={{
              width: 'fit-content',
              backgroundColor: '#643E91',
              color: '#fff',
            }}
          >
            {'Upload Imagem'}
            <input
              className='file-input'
              value={""}
              type="file"
              accept="image/png, image/jpeg"
              onChange={(e) => {
                e.target.files && getBase64(e.target.files[0], (base64) => {
                  setUser(prev => prev ? { ...prev, foto: base64 } : prev);
                });
              }}
            />

          </Button>
          <Button
            variant='outlined'
            color='success'
            size='small'
            onClick={() => setOpenModal(false)}
            endIcon={<SaveIcon />}>
            {t('save')}
          </Button>
          <Button
            size='small'
            endIcon={<ClearIcon />}
            variant='outlined'
            color='warning'
            onClick={() => {
              trackEventMatomo('modal edição de usuário', 'click', ' button', 'cancela');
              setUser(prev => prev);
              setOpenModal(false);
            }}>
            {t('cancel')}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

const ModalChangePassword = ({ openModal, setOpenModal, userEmail }: { openModal: boolean, setOpenModal: Function, userEmail: string }) => {
  const { t } = useTranslation('translation');
  const [password, setPassword] = useState<string | null>(null);
  const [validPassword, setValidPassword] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  function RegrasSenha({ regra, label, valor }: { regra: boolean, label: string, valor: number }) {
    return (
      regra && valor > 0 && <Typography fontSize='small' color='textDisabled' fontWeight={600}>{label}</Typography>
    );
  }

  const { data: passwordRules, isLoading: loadingPassRules } = useQuery<PasswordRules | undefined>({
    queryKey: ['GET_REGRAS'],
    queryFn: async () => {
      const token = await AttToken();
      if (token) {
        const body = { ...token, empresa: token.userEmpresa };
        const response: PasswordRules = await companySrvc.getAllPoliticaSenha(body);
        return response;
      }
    },
    throwOnError: (error: unknown) => {
      toast.error(t('gestaoUsuarios.erroBuscaRegra'), { toastId: 'fetch-password-rules-error' });
      return true;
    },
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    staleTime: 1000 * 60 * 5,
  });

  const { mutate: changePassword, isPending: isChangingPassword } = useMutation({
    mutationKey: ['CHANGE_PASSWORD'],
    mutationFn: async () => {
      if (validPassword !== password) return toast.warning(`${t('users.userRegister.passwordWarning')}`);
      if (!passwordIsValid) { return toast.warning(`${t('gestaoUsuarios.senhaNaoAtende')}`); };
      try {
        const promise = toast.promise(async () => {
          const token = await AttToken();
          if (!token) throw new Error("Token not found");
          if (token && userEmail && password) {
            const response = await loginService.changePassword(token, userEmail, password, sendEmail);
            if (!response) throw new Error("Error changing password");
            if (response.success === false) {
              throw new Error(response.message);
            }
            return response;
          }
        }, {
          pending: t('users.userChangeModal.changingPassword'),
          success: t('users.userChangeModal.passwordChanged'),
          error: t('users.userChangeModal.errorChangingPassword'),
        }, { toastId: 'changing_password' });
        return promise;
      } catch (e) {
        toast.error(`${t('users.userChangeModal.errorChangingPassword')}`);
      }
    },
    onSuccess: () => {
      setOpenModal(false);
    }
  });

  function checkPasswordRules(value: PasswordRules | undefined, senha: string | null) {
    if (!value || !senha) {
      return {
        digit: false,
        lowercase: false,
        uppercase: false,
        special: false,
        length: false,
      };
    }

    const rules = {
      digit: value.digit > 0 ? new RegExp(`(?:[^0-9]*[0-9]){${value.digit}}`) : null,
      lowercase: value.lowercase > 0 ? new RegExp(`(?:[^a-z]*[a-z]){${value.lowercase}}`) : null,
      uppercase: value.uppercase > 0 ? new RegExp(`(?:[^A-Z]*[A-Z]){${value.uppercase}}`) : null,
      special: value.special > 0 ? new RegExp(`(?:[^!@#$%^&*(),.?":{}|<>]*[!@#$%^&*(),.?":{}|<>]){${value.special}}`) : null,
      length: value.min > 0 ? new RegExp(`(.{${value.min},})`) : null,
    };

    return {
      digit: !rules.digit || rules.digit.test(senha),
      lowercase: !rules.lowercase || rules.lowercase.test(senha),
      uppercase: !rules.uppercase || rules.uppercase.test(senha),
      special: !rules.special || rules.special.test(senha),
      length: !rules.length || rules.length.test(senha),
    };
  }

  const passwordIsValid = Object.values(checkPasswordRules(passwordRules, password)).every(Boolean);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={openModal}
      onClose={() => setOpenModal(false)}
      scroll={'paper'}

    >
      <DialogTitle>
        {t('users.userChangeModal.changePassword')}
        <IconButton
          aria-label="close"
          onClick={() => setOpenModal(false)}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ paddingTop: 2, gap: 1.5, display: 'flex', flexDirection: 'column' }}>
        <FormControlLabel
          label={t('editUserModal.sendNewPasswordEmail')} 
          control={<Checkbox checked={sendEmail} onChange={() => setSendEmail(!sendEmail)} />}
        />
        <TextField
          size='small'
          fullWidth
          onClick={() => trackEventMatomo('cadastro de usuário', 'click', 'input', 'edita valor')}
          required
          type={showPassword ? 'text' : 'password'}
          value={password}
          label={t('loginScreen.password')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const senha = e.target.value.trim();
            setPassword(senha);
            checkPasswordRules(passwordRules, senha);
          }}
          onPaste={(e) => e.preventDefault()}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    edge="end"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff /> }
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
           size='small'
           fullWidth
           onClick={() => trackEventMatomo('cadastro de usuário', 'click', 'input', 'edita valor')}
           autoComplete='new-password'
           required
           value={validPassword ?? ''}
           type={showPassword ? 'text' : 'password'}
           label={t('users.userRegister.confirmation')}
           onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValidPassword(e.target.value.trim())}
           onPaste={(e) => e.preventDefault()}
        />
        {loadingPassRules
          ? <Skeleton variant='text' width={250} height={35} />
          : (() => {
            if (!passwordRules) return null;
            const rules = checkPasswordRules(passwordRules, password);
            return (
              <Box>
                <Typography fontSize='medium' fontWeight={600} color='textDisabled'>{t('users.userRegister.passwordRules')}</Typography>
                <RegrasSenha valor={passwordRules.digit} label={`*${t('settingsPage.password.minNmbr')} ${passwordRules.digit}`} regra={!rules.digit} />
                <RegrasSenha valor={passwordRules.lowercase} label={`*${t('settingsPage.password.minLower')} ${passwordRules.lowercase}`} regra={!rules.lowercase} />
                <RegrasSenha valor={passwordRules.uppercase} label={`*${t('settingsPage.password.minUpper')} ${passwordRules.uppercase}`} regra={!rules.uppercase} />
                <RegrasSenha valor={passwordRules.special} label={`*${t('settingsPage.password.minSpecial')} ${passwordRules.special}`} regra={!rules.special} />
                <RegrasSenha valor={passwordRules.min} label={`*${t('settingsPage.password.minChar')} ${passwordRules.min}`} regra={!rules.length} />
                {password && password.length > 0 && Object.values(rules).every(Boolean) && <Typography color='textDisabled'>{t('gestaoUsuarios.senhaAtende')}</Typography>}
              </Box>
            );
          })()}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          variant='contained'
          color='info'
          onClick={() => setOpenModal(false)}
        >
          {t('cancel')}
        </Button>
        <Button
          disabled={isChangingPassword || !passwordIsValid || validPassword !== password}
          variant='contained'
          color='success'
          onClick={() => changePassword()}
          endIcon={<SaveIcon />}
        >
          {t('users.userChangeModal.changePassword')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const tagsService = new Companies();
const teamsService = new Teams();
const userService = new Users();
export default function ChangeUserModal({ userId, openModal, setOpenModal }: { userId: string, openModal: boolean, setOpenModal: Function }) {
  const { t } = useTranslation('translation');
  const { userData: loggedUserData } = useContext(UserContext);
  const { accessToken } = useContext(AuthContext);
  const [value, setValue] = useState(0);
  const [openModalChangePassword, setOpenModalChangePassword] = useState(false);

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };


  const [user, setUser] = useState<userType | null>(null);
  const [openModalImg, setOpenModalImg] = useState<boolean>(false);
  const role = loggedUserData.role;
  const isAdm = loggedUserData.adm ?? false;

  const { data: userData, isLoading: isLoadingUserData, isFetching: isFetchingUserData } = useQuery({
    queryKey: ['GET_USER', userId],
    queryFn: async () => {
      try {
        const response = toast.promise(async () => {
          const token = await AttToken();
          if (!token) throw new Error('Token not found');
          if (token) {
            const response = await userService.getUserById(token, userId);
            if(!response) throw new Error('Error fetching user data');
            handleUser(response.data.user as userType);
            return response;
          }
        }, {
          pending: t('gestaoUsuarios.buscaUsuarioId'),
          error: t('gestaoUsuarios.erroBuscaUsuarioId')
        }, { toastId: 'fetch-user-toast' });
        return response;
      } catch (err) {
        throw new Error('Error fetching user data');
      }
    },
    retry: 2,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  const { data: teamsData } = useQuery({
    queryKey: ['GET_TEAMS', userId],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token not found');
        if (token) {
          const response = await teamsService.getSimpleTeams(token);
          if (!response) throw new Error('Error fetching teams data');
          return response;
        }
      } catch (err) {
        toast.error(t('listaEquipes.erroBuscaEquipe'), { toastId: 'fetch-teams-error' });
        throw new Error('Error fetching teams data');
      }
    },
    retry: 2,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  const { data: tagsData, isLoading: isTagsLoading, isFetching: isTagsFetching } = useQuery({
    queryKey: ['GET_TAGS', userId],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token not found');
        if (token) {
          const response = await tagsService.getAllTags(token);
          if (!response) throw new Error('Error fetching tags data');
          return response.tags;
        }
      } catch (err) {
        throw new Error('Error fetching tags data');
      }
    },
    retry: 2,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  const { mutate: onSubmit, isPending: isSubmitting } = useMutation({
    mutationKey: ['CHANGE_USER'],
    mutationFn: async () => {
      try {
        const response = toast.promise(async () => {
          const token = await AttToken();
          if (!token) throw new Error('Token not found');
          if (token) {
            if (user?.foto === '') {
              delete user.foto;
            }
            const response = await userService.changeUser(token, userId, user as userType);
            if (!response) {
              throw new Error('Error updating user');
            }
            return response;
          }
        }, {
          pending: t('users.userChangeModal.alterUser', { toastId: 'erroUpdate' }),
          success: t('users.userChangeModal.userAltered', { toastId: 'sucessoUpdate' }),
          error: t('users.userChangeModal.errorAlterUser', { toastId: 'erroUpdate' })
        }, { toastId: 'update-user-toast' });
        return response;
      } catch (err) {
        throw new Error('Error updating user');
      }
    },
    onSuccess: () => {
      setOpenModal(false);
      queryClient.resetQueries({ queryKey: ['GET_USERS'] });
    }
  });

  const { mutate, isPending: isDeleting } = useMutation({
    mutationKey: ['DELETE_USER'],
    mutationFn: async () => {
      const promise = toast.promise(async () => {
        const token = await AttToken();
        if (token) {
          const res = await userService.deleteUser(token, userId);
          return res;
        }
      }, {
        pending: t('users.userChangeModal.deletingUser'),
        success: t('users.userChangeModal.userDeleted'),
        error: t('users.userChangeModal.errorDeleteUser')
      }, { toastId: 'deleting-user' });
      return promise;
    },
    onSuccess: () => {
      setOpenModal(false);
      queryClient.resetQueries({ queryKey: ['GET_USERS'] });
    }
  });

  const { mutate: reset, isPending: isResetting } = useMutation({
    mutationKey: ['resetPassword'],
    mutationFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error('Token not found');
      const response = await userService.resetUserPassword(token, userId);
      if (!response) throw new Error('Error resetting password');
      return response;
    },
    onMutate: () => {
      toast.loading(t('users.userChangeModal.resettingPassword'), { toastId: 'resetting-password-toast' });
    },
    onSuccess: () => {
      toast.update('resetting-password-toast', {
        render: t('users.userChangeModal.resetMsg'),
        type: 'success',
        isLoading: false,
        autoClose: 5000,
      });
      setOpenModal(false);
    },
    onError: () => {
      toast.update('resetting-password-toast', {
        render: t('users.userChangeModal.resetError'),
        type: 'error',
        isLoading: false,
        autoClose: 5000,
      });
    }
  });

  const {
    data: sessions = [],
    isLoading: isLoadingSessions,
    isFetching: isFetchingSessions,
    isError: isSessionsError,
  } = useQuery({
    queryKey: ['GET_USER_SESSIONS', userId],
    queryFn: async () => {
      if (!accessToken) throw new Error('Sessão de autenticação indisponível.');

      return authService.listUserSessions(accessToken, userId);
    },
    enabled: openModal && isAdm && Boolean(userId),
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const { mutate: logoutUserSession, isPending: isLoggingOutSession } = useMutation({
    mutationKey: ['LOGOUT_USER_SESSION', userId],
    mutationFn: async (sessionId: string) => {
      if (!accessToken) throw new Error('Sessão de autenticação indisponível.');

      return authService.logoutUserSession(accessToken, userId, sessionId);
    },
    onSuccess: async () => {
      toast.success('Sessão encerrada com sucesso.', { toastId: 'user-session-logout-success' });
      await queryClient.invalidateQueries({ queryKey: ['GET_USER_SESSIONS', userId] });
    },
    onError: () => {
      toast.error('Não foi possível encerrar a sessão.', { toastId: 'user-session-logout-error' });
    },
  });

  const onLoading = isLoadingUserData || isFetchingUserData || isSubmitting || isDeleting || isResetting || isTagsLoading || isTagsFetching;

  function handleUser(data: userType) {
    setUser({
      adm: data?.adm ?? false,
      aprovador: data?.aprovador ?? false,
      contato: data?.contato ?? '',
      email: data?.email ?? '',
      empresa: data?.empresa ?? '',
      foto: data?.foto ?? '',
      gestorPA: data?.gestorPA ?? false,
      id: data?.id ?? '',
      inspetorPA: data?.inspetorPA ?? false,
      listaEquipes: data?.listaEquipes ?? [],
      matricula: data?.matricula ?? '',
      nome: data?.nome ?? '',
      perfil: data?.perfil ?? '',
      sendEmail: data?.sendEmail ?? false,
      status: data?.status ?? 'ativo',
      tags: data?.tags ?? [],
    });
  };

  const [openDelete, setOpenDelete] = useState(false);

  return (
    <>
      {openModalImg && <ModalImage openModal={openModalImg} setOpenModal={setOpenModalImg} setUser={setUser} user={user as userType} />}
      {openDelete && <ModalDelete openDelete={openDelete} setOpenDelete={setOpenDelete} mutate={mutate} user={userId} name={user?.nome as string} />}
      {openModalChangePassword && user?.email && <ModalChangePassword openModal={openModalChangePassword} setOpenModal={setOpenModalChangePassword} userEmail={user.email} /> }
      <Dialog
        fullWidth
        maxWidth={'md'}
        open={openModal}
        onClose={() => setOpenModal(false)}
        scroll={'paper'}
      >
        <DialogTitle>  {t('users.userChangeModal.alterData')}</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setOpenModal(false)}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers={true}>
          <Tabs
            textColor="secondary"
            indicatorColor="secondary"
            value={value} onChange={handleChangeTab}>
            <Tab disabled={onLoading} label={t('editUserModal.userData')} value={0} />
            {isAdm
              && <Tab disabled={onLoading} label={t('editUserModal.sessionInfo')} value={1} />}
          </Tabs>
          <CustomTabPanel value={value} index={0}>
            <DialogContent>
              {onLoading
                ? <Loading />
                : <EditUser
                  user={user}
                  setUser={setUser}
                  isLoading={onLoading}
                  role={role}
                  isAdm={isAdm}
                  tags={tagsData ?? []}
                  teams={teamsData as SimpleTeam[]}
                  setOpenModalImg={setOpenModalImg} />}
            </DialogContent>
            <DialogActions>
              <>
                {isAdm &&
                  <Button
                    variant='contained'
                    color='error'
                    disabled={onLoading}
                    endIcon={<DeleteIcon />}
                    onClick={() => {
                      trackEventMatomo('modal edição de usuário', 'click', ' button', 'exclui usuario');
                      setOpenDelete(true);
                    }}
                  >
                    {t('users.delete')}
                  </Button>
                }
                <Button
                  color='warning'
                  variant='contained'
                  disabled={onLoading}
                  endIcon={<LockResetIcon />}
                  onClick={() => {
                    trackEventMatomo('modal edição de usuário', 'click', ' button', 'reseta senha');
                    reset();
                  }}
                >
                  {t('users.userChangeModal.resetPassword')}
                </Button>
                {isAdm &&
                  <Button
                    variant='contained'
                    color='info'
                    disabled={onLoading}
                    endIcon={<RestartAltIcon />}
                    onClick={() => {
                      trackEventMatomo('modal edição de usuário', 'click', ' button', 'altera senha do usuário');
                      setOpenModalChangePassword(true);
                    }}
                  >
                    {t('users.userChangeModal.changePassword')}
                  </Button>
                }
                <Button
                  variant='contained'
                  color='success'
                  disabled={onLoading}
                  endIcon={<SaveIcon />}
                  onClick={(event) => {
                    trackEventMatomo('modal edição de usuário', 'click', ' button', 'salva');
                    event.preventDefault();
                    onSubmit();
                  }}
                >
                  {t('save')}
                </Button>
              </>
            </DialogActions>
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <SessionInfo
              sessions={sessions}
              isLoading={isLoadingSessions || isFetchingSessions}
              isLoggingOut={isLoggingOutSession}
              isError={isSessionsError}
              onLogout={logoutUserSession}
            />
          </CustomTabPanel>
        </DialogContent>
      </Dialog >
    </>
  );
}
